import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { API_BASE, getProfileAssetUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Plus, Search, Edit, Trash2, GraduationCap, Mail, Phone, Upload, FileSpreadsheet, Download, ChevronDown, UserPlus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Students = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkClassId, setBulkClassId] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    admission_no: '',
    class_id: '',
    section_id: '',
    date_of_birth: '',
    gender: '',
    address: '',
  });

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, [selectedClass, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchStudents();
      fetchClasses();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [selectedClass, user?.school_id]);

  const fetchStudents = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        ...(selectedClass ? { class_id: selectedClass } : {}),
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const response = await api.get('/students', { params });
      setStudents(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch students', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const response = await api.get('/classes', { params });
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await api.put(`/students/${editingStudent.id}`, formData);
        toast({ title: 'Success', description: 'Student updated successfully' });
      } else {
        const res = await api.post('/students', { ...formData });
        toast({ title: 'Success', description: res.data?.message || 'Student created successfully.' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Operation failed',
        variant: 'destructive' 
      });
    }
  };

  const handleEdit = (student) => {
    navigate(`/students/${student.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await api.delete(`/students/${id}`);
        toast({ title: 'Success', description: 'Student deleted successfully' });
        fetchStudents();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to delete student', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', admission_no: '',
      class_id: '', section_id: '', date_of_birth: '', gender: '', address: '',
    });
    setEditingStudent(null);
  };

  const downloadSample = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE}/api/students/sample?format=${format}`;
      const res = await fetch(url, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `students_sample.${format}`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast({ title: 'Download started', description: `Sample ${format.toUpperCase()} file downloaded.` });
    } catch (err) {
      toast({ title: 'Download failed', description: err?.message || 'Could not download sample.', variant: 'destructive' });
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast({ title: 'No file', description: 'Please select a CSV or Excel file.', variant: 'destructive' });
      return;
    }
    if (!bulkClassId) {
      toast({ title: 'Class required', description: 'Please select a class for the students.', variant: 'destructive' });
      return;
    }
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('class_id', bulkClassId);
      const res = await api.post('students/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data || {};
      const created = data.created ?? 0;
      const failed = data.failed || [];
      setBulkFile(null);
      setBulkClassId('');
      setBulkDialogOpen(false);
      if (document.getElementById('bulk-file-input')) document.getElementById('bulk-file-input').value = '';
      fetchStudents();
      if (failed.length > 0) {
        toast({
          title: 'Bulk upload completed',
          description: `${created} student(s) added. ${failed.length} row(s) failed. Check details below.`,
          variant: failed.length === (data.total_rows || 0) ? 'destructive' : 'default',
        });
      } else {
        toast({ title: 'Success', description: `${created} student(s) added successfully.` });
      }
    } catch (err) {
      toast({
        title: 'Bulk upload failed',
        description: err.response?.data?.message || err?.message || 'Could not upload file.',
        variant: 'destructive',
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const filteredStudents = students.filter(s => 
    `${s.first_name} ${s.last_name} ${s.admission_no} ${s.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Students</h1>
          <p className="text-slate-400">Manage student records</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-student-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Student
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700 text-white">
            <DropdownMenuItem
              className="cursor-pointer focus:bg-slate-700 focus:text-white"
              onSelect={(e) => { e.preventDefault(); setIsDialogOpen(true); resetForm(); }}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Single upload
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer focus:bg-slate-700 focus:text-white"
              onSelect={(e) => { e.preventDefault(); setBulkDialogOpen(true); }}
            >
              <Upload className="w-4 h-4 mr-2" />
              <span className="flex-1">Bulk upload</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    required
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    disabled={!!editingStudent}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Admission No</Label>
                  <Input
                    value={formData.admission_no}
                    onChange={(e) => setFormData({...formData, admission_no: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    disabled={!!editingStudent}
                    placeholder="Auto-generated if empty"
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Class</Label>
                  <Select value={formData.class_id} onValueChange={(v) => setFormData({...formData, class_id: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {classes.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                {editingStudent ? 'Update Student' : 'Create Student'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        {/* Bulk upload dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={(open) => { setBulkDialogOpen(open); if (!open) { setBulkFile(null); setBulkClassId(''); } }}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" /> Bulk upload students
              </DialogTitle>
              <p className="text-slate-400 text-sm font-normal">
                Upload a CSV or Excel file with student data. Select the class below — all students will be added to that class. Only "Student Name" column is required.
              </p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-300 mb-1 block">Class *</Label>
                <Select value={bulkClassId} onValueChange={setBulkClassId}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select class for all students" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {classes.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  id="bulk-file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => document.getElementById('bulk-file-input')?.click()}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> Choose file
                </Button>
                {bulkFile && <span className="text-sm text-slate-400">{bulkFile.name}</span>}
              </div>
              <Button
                type="button"
                onClick={handleBulkUpload}
                disabled={!bulkFile || !bulkClassId || bulkUploading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {bulkUploading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2" />
                    Uploading…
                  </>
                ) : (
                  'Upload Students'
                )}
              </Button>
              <p className="text-slate-500 text-sm flex items-center gap-2 flex-wrap">
                <Download className="w-4 h-4" />
                Download sample:
                <button
                  type="button"
                  onClick={() => downloadSample('csv')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  CSV
                </button>
                <span className="text-slate-600">|</span>
                <button
                  type="button"
                  onClick={() => downloadSample('xlsx')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Excel (.xlsx)
                </button>
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Select value={selectedClass || "all"} onValueChange={(v) => setSelectedClass(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">All Classes</SelectItem>
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredStudents.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No students found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredStudents.map((student) => (
            <Card key={student.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/students/${student.id}`}
                    className="flex items-center gap-4 flex-1 min-w-0"
                  >
                    <Avatar className="w-12 h-12 shrink-0">
                      <AvatarImage 
                        src={getProfileAssetUrl(student.avatar)} 
                        alt={`${student.first_name} ${student.last_name}`}
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-blue-500/20 text-blue-400 font-semibold">
                        {student.first_name?.[0]?.toUpperCase()}{student.last_name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white hover:text-blue-300 transition-colors">
                        {student.first_name} {student.last_name}
                      </h3>
                      <p className="text-sm text-slate-400">
                        {student.admission_no && !student.admission_no.startsWith('ADM-') ? student.admission_no : ''}{student.admission_no && !student.admission_no.startsWith('ADM-') ? ' • ' : ''}{student.class_name || 'No class assigned'}
                      </p>
                      <div className="flex items-center gap-4 mt-1">
                        {student.email && !student.email.endsWith('@noemail.local') && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {student.email}
                        </span>
                        )}
                        {student.phone && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {student.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.preventDefault(); handleEdit(student); }}
                      className="text-slate-400 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.preventDefault(); handleDelete(student.id); }}
                      className="text-slate-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Students;
